import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { drawdownAPI, fundAPI } from '../../services/api';
import { Plus, Search, FileText, Trash2, ArrowLeft, Calendar, Percent, AlertCircle, Layers, TrendingUp, Wallet, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDateFormat } from '../../hooks/useDateFormat';

/**
 * Drawdown Management (two-view page)
 *
 * DEFAULT: a responsive grid of per-Fund summary tiles (data from
 * `/api/drawdowns/by-fund/summary`), plus the global page header,
 * search, filter, and "New Drawdown" button.
 *
 * DRILL-DOWN: `?fund={fund_id}` in the URL swaps the grid out for the
 * existing drawdown table filtered to that fund. The table, its
 * columns, row-click, status pill, delete action, and class-wise %
 * display are unchanged — only the shell around them is different.
 *
 * Search behaviour: a non-empty search term collapses the grid and
 * shows a flat drawdown table across ALL funds so that ops can find a
 * specific drawdown without first entering a fund. Clearing search
 * (and no `?fund=`) returns to the grid.
 */
const statusColors = {
  draft: 'bg-gray-100 text-gray-600',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};
const statusLabel = (s) => (s || '').replace(/_/g, ' ');

// Currency helpers — funds may differ, so every amount MUST be formatted
// against its own fund's currency. Fall back to just the numeric string
// (no prefix) when no currency is set anywhere.
const CURRENCY_SYMBOL = { INR: '₹', USD: '$' };
const fmtMoney = (v, currency) => {
  const n = Number(v || 0).toLocaleString();
  if (!currency) return n;
  const sym = CURRENCY_SYMBOL[currency];
  // Prefer the symbol when we have one; always suffix the ISO code so the
  // number is unambiguous ("₹ 1,00,000 INR" — trader-friendly).
  return sym ? `${sym} ${n}` : `${currency} ${n}`;
};

const Drawdowns = () => {
  const fmtDate = useDateFormat();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFundId = searchParams.get('fund') || '';

  const [drawdowns, setDrawdowns] = useState([]);
  const [funds, setFunds] = useState([]);
  const [fundSummary, setFundSummary] = useState(null); // { funds, total_drawdowns, total_called, total_received }
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [autoReviewOnly, setAutoReviewOnly] = useState(false);
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const loadData = useCallback(async () => {
    try {
      // Always load both — cheap, keeps the grid + list in sync when the
      // user toggles between them via URL / back button.
      const [ddRes, fundsRes, summaryRes] = await Promise.all([
        drawdownAPI.getAll(activeFundId || undefined),
        fundAPI.getAll(),
        drawdownAPI.getByFundSummary(),
      ]);
      setDrawdowns(ddRes.data);
      setFunds(fundsRes.data);
      setFundSummary(summaryRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeFundId]);

  useEffect(() => { loadData(); }, [loadData]);

  const goToFund = (fundId) => {
    setSearchParams(fundId ? { fund: fundId } : {});
  };
  const backToGrid = () => setSearchParams({});

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this drawdown?')) return;
    try {
      await drawdownAPI.delete(id);
      setDrawdowns(prev => prev.filter(d => d.id !== id));
      // Also refresh the fund summary so tiles update.
      drawdownAPI.getByFundSummary().then(r => setFundSummary(r.data)).catch(() => {});
    } catch { alert('Failed to delete'); }
  };

  // Filtered drawdowns for the list view (used in drill-down + search mode).
  const filtered = useMemo(() => drawdowns.filter(d => {
    if (searchTerm && !d.drawdown_name.toLowerCase().includes(searchTerm.toLowerCase())
                    && !d.drawdown_number.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (statusFilter && d.status !== statusFilter) return false;
    if (autoReviewOnly && !(d.auto_added_pending_count > 0)) return false;
    return true;
  }), [drawdowns, searchTerm, statusFilter, autoReviewOnly]);

  const totalAutoReviewPending = drawdowns.reduce((s, d) => s + (d.auto_added_pending_count || 0), 0);

  // Fund tile matching the current drill-down, if any. When present, we
  // reuse its called/received/outstanding for the three summary cards so
  // the numbers on the detail view match the tile the user just clicked
  // (single source of truth).
  const activeFundTile = fundSummary && activeFundId
    ? (fundSummary.funds || []).find(f => f.fund_id === activeFundId) || null
    : null;

  // Grid view = default page (no fund filter, no search).
  const showGrid = !activeFundId && !searchTerm;
  const activeFund = activeFundId ? funds.find(f => f.id === activeFundId) : null;

  if (loading) return <div className="p-8 text-center text-gray-500">Loading drawdowns...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="drawdowns-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          {activeFundId && activeFund ? (
            <>
              <button
                onClick={backToGrid}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mb-1"
                data-testid="dd-back-to-funds"
              >
                <ArrowLeft size={14} /> Back to funds
              </button>
              <h1 className="text-2xl font-bold text-gray-900">{activeFund.name}</h1>
              <p className="text-sm text-gray-500 mt-1">Drawdowns for this fund</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Drawdown Management</h1>
              <p className="text-sm text-gray-500 mt-1">Create and manage capital drawdowns for your funds</p>
            </>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/drawdowns/create')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            data-testid="create-drawdown-btn"
          >
            <Plus size={16} className="mr-2" /> New Drawdown
          </button>
        )}
      </div>

      {/* Search + Filters — always visible */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search drawdowns across all funds…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            data-testid="search-drawdowns"
          />
        </div>
        <select
          value={activeFundId}
          onChange={e => goToFund(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
          data-testid="filter-fund"
        >
          <option value="">All Funds</option>
          {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
          data-testid="filter-status"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="active">Active (legacy)</option>
          <option value="completed">Completed</option>
        </select>
        {totalAutoReviewPending > 0 && (
          <button
            type="button"
            onClick={() => setAutoReviewOnly(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium border transition-colors ${
              autoReviewOnly
                ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
            }`}
            title="Show only drawdowns containing auto-added investor rows that still need review"
            data-testid="filter-auto-added-review"
          >
            <AlertCircle size={14} />
            Auto-added pending review
            <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
              autoReviewOnly ? 'bg-white text-amber-700' : 'bg-amber-200 text-amber-800'
            }`}>
              {totalAutoReviewPending}
            </span>
          </button>
        )}
      </div>

      {/* Summary cards — AMC-wide on the grid, fund-scoped on the drill-
          down. In fund-detail mode we reuse THAT fund's tile figures
          (from the same `/by-fund/summary` payload) so the three cards
          exactly match the tile the user clicked. Currency is per-fund
          in detail mode; on the grid we intentionally show no symbol
          because funds may have mixed currencies. */}
      {fundSummary && (
        activeFundTile ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border rounded-lg p-4" data-testid="dd-fund-scope-total-drawdowns">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Drawdowns</p>
              <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">{activeFundTile.drawdown_count}</p>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="dd-fund-scope-total-called">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Called Amount</p>
              <p className="text-2xl font-bold text-blue-600 mt-1 font-mono">
                {fmtMoney(activeFundTile.total_called, activeFundTile.fund_currency)}
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="dd-fund-scope-total-received">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Received</p>
              <p className="text-2xl font-bold text-green-600 mt-1 font-mono">
                {fmtMoney(activeFundTile.total_received, activeFundTile.fund_currency)}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border rounded-lg p-4" data-testid="dd-global-total-drawdowns">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Drawdowns</p>
              <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">{fundSummary.total_drawdowns}</p>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="dd-global-total-called">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Called Amount</p>
              <p className="text-2xl font-bold text-blue-600 mt-1 font-mono">{fundSummary.total_called.toLocaleString()}</p>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="dd-global-total-received">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Received</p>
              <p className="text-2xl font-bold text-green-600 mt-1 font-mono">{fundSummary.total_received.toLocaleString()}</p>
            </div>
          </div>
        )
      )}

      {/* ─── VIEW 1: Fund tile grid ─────────────────────────────────── */}
      {showGrid && (
        (!fundSummary || fundSummary.funds.length === 0) ? (
          <div className="text-center py-12 bg-white border rounded-lg" data-testid="dd-empty-grid">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No drawdowns yet. Create your first drawdown to see per-fund tiles here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dd-fund-grid">
            {fundSummary.funds
              .filter(ft => !statusFilter || (ft.status_counts?.[statusFilter] || 0) > 0)
              .map(ft => {
              const called = ft.total_called || 0;
              const received = ft.total_received || 0;
              const outstanding = ft.outstanding || 0;
              const statusChips = Object.entries(ft.status_counts || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3);
              return (
                <button
                  key={ft.fund_id}
                  onClick={() => goToFund(ft.fund_id)}
                  className="text-left bg-white border rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all group"
                  data-testid={`dd-fund-tile-${ft.fund_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold text-gray-900 truncate group-hover:text-blue-600" title={ft.fund_name}>
                        {ft.fund_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        <span className="inline-flex items-center gap-1"><Layers size={11} />{ft.drawdown_count} drawdown{ft.drawdown_count !== 1 ? 's' : ''}</span>
                        {ft.fund_currency && <span className="ml-2 font-mono uppercase text-gray-400" data-testid={`dd-fund-currency-${ft.fund_id}`}>{ft.fund_currency}</span>}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 flex-shrink-0" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 inline-flex items-center gap-1"><TrendingUp size={12} className="text-blue-500" />Total Called</span>
                      <span className="font-semibold text-blue-600 font-mono" data-testid={`dd-fund-called-${ft.fund_id}`}>{fmtMoney(called, ft.fund_currency)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 inline-flex items-center gap-1"><Wallet size={12} className="text-green-500" />Total Received</span>
                      <span className="font-semibold text-green-600 font-mono" data-testid={`dd-fund-received-${ft.fund_id}`}>{fmtMoney(received, ft.fund_currency)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-1.5 border-t">
                      <span className="text-gray-500">Outstanding</span>
                      <span className={`font-semibold font-mono ${outstanding > 0 ? 'text-orange-600' : 'text-gray-400'}`} data-testid={`dd-fund-outstanding-${ft.fund_id}`}>
                        {fmtMoney(outstanding, ft.fund_currency)}
                      </span>
                    </div>
                  </div>
                  {statusChips.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                      {statusChips.map(([st, ct]) => (
                        <span key={st} className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusColors[st] || 'bg-gray-100 text-gray-600'}`}>
                          {ct} {statusLabel(st)}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )
      )}

      {/* ─── VIEW 2: Drawdown list (drill-down OR search mode) ──────── */}
      {!showGrid && (
        filtered.length === 0 ? (
          <div className="text-center py-12 bg-white border rounded-lg">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">
              {searchTerm ? `No drawdowns match "${searchTerm}".` : 'No drawdowns yet on this fund.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border rounded-lg overflow-hidden" data-testid="dd-list-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                <th className="px-4 py-3">Drawdown No.</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Fund</th>
                <th className="px-4 py-3 text-center">% Called</th>
                <th className="px-4 py-3">Due Date</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Received</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 w-16"></th>
              </tr></thead>
              <tbody>
                {filtered.map(dd => (
                  <tr key={dd.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/drawdowns/${dd.id}`)} data-testid={`drawdown-row-${dd.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">
                      {dd.drawdown_number}
                      {dd.auto_added_pending_count > 0 && (
                        <span
                          className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold align-middle"
                          title={`${dd.auto_added_pending_count} auto-added investor row(s) need review`}
                          data-testid={`auto-review-badge-${dd.id}`}
                        >
                          <AlertCircle size={10} /> {dd.auto_added_pending_count}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{dd.drawdown_name}</td>
                    <td className="px-4 py-3 text-gray-600">{dd.fund_name}</td>
                    <td className="px-4 py-3 text-center">
                      {dd.class_percentages && dd.class_percentages.length > 0 ? (
                        <div className="inline-flex flex-col items-center gap-0.5 text-xs" data-testid={`dd-row-class-pct-${dd.id}`}>
                          {dd.class_percentages.map((cp, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 text-blue-700">
                              <span className="font-medium text-gray-700">{cp.class_name}:</span>
                              <span className="font-semibold">{cp.percentage_called}%</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                          <Percent size={12} />{dd.percentage_called}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600"><span className="inline-flex items-center gap-1"><Calendar size={12} />{fmtDate(dd.due_date) || '-'}</span></td>
                    <td className="px-4 py-3 text-right font-medium font-mono">{(dd.total_drawdown_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium font-mono">{(dd.total_received_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[dd.status] || 'bg-gray-100'}`} data-testid={`status-pill-${dd.id}`}>{statusLabel(dd.status)}</span></td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      {canEdit && <button onClick={e => handleDelete(dd.id, e)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-drawdown-${dd.id}`}><Trash2 size={14} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default Drawdowns;
